// owner/api/ownerReads.js — every server read the owner screens make, on secureApi (the one request path,
// tasks/owner-mobile-app-build.md §10 step 8 decision 3; publicApi for the one public answer, the store link). Each read is owner-scoped on the server; nothing here
// filters by owner. Dates, months and "today" always come from the server — this file does no date arithmetic.

import { useQuery, useQueries, useInfiniteQuery } from '@tanstack/react-query';
import { secureApi } from '../../services/secureApi';
import ownerReportApi from '../../services/ownerReportApi';
import { notificationApi } from '../../services/notificationApi';
import { publicApiService } from '../../services/api/publicApi';

const get = (url, params) => secureApi.get(url, { params }).then((res) => res.data);

/** The signed-in owner's profile (name for the greeting). */
export const useOwnerProfile = () => useQuery({
  queryKey: ['owner', 'me'],
  queryFn: () => get('/api/v1/users/me').then((data) => data.user),
  staleTime: 5 * 60 * 1000
});

/** Every unit the owner actively owns (all at once: fine to 250, API_DATA_FETCHING_PATTERNS). */
export const useOwnerUnits = () => useQuery({
  queryKey: ['owner', 'units'],
  queryFn: () => get('/api/v1/properties')
});

/** One unit, owner view. */
export const useOwnerUnit = (unitId) => useQuery({
  queryKey: ['owner', 'unit', unitId],
  queryFn: () => get(`/api/v1/properties/${unitId}`),
  enabled: Boolean(unitId)
});

// The unit-status route takes at most 200 ids per call (be/routes/unitStatusRoutes.js MAX_BATCH_IDS).
const STATUS_BATCH = 200;

/**
 * «حالة التشغيل» for many units, read in batches of 200. Returns { byId, isLoading, isError, refetch }; a unit
 * missing from byId while loading shows no chip, never a wrong one.
 */
export const useUnitStatuses = (unitIds = []) => {
  const batches = [];
  for (let i = 0; i < unitIds.length; i += STATUS_BATCH) batches.push(unitIds.slice(i, i + STATUS_BATCH));
  const results = useQueries({
    queries: batches.map((ids) => ({
      queryKey: ['owner', 'unit-status', ids],
      queryFn: () => get('/api/v1/unit-status', { propertyIds: ids.join(',') }).then((data) => data.unitStatuses || {})
    }))
  });
  return {
    byId: Object.assign({}, ...results.map((r) => r.data || {})),
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
    refetch: () => results.forEach((r) => r.refetch())
  };
};

/**
 * Revenue, occupancy, bookings and the platform / month split for a named server period (`thisMonth`,
 * `last6Months`, be/utils/reportingWindows.js), for all units or one.
 */
export const usePortfolio = (period, unitId) => useQuery({
  queryKey: ['owner', 'portfolio', period, unitId || 'all'],
  queryFn: () => get('/api/v1/revenue/portfolio/metrics', { period, ...(unitId ? { propertyIds: unitId } : {}) })
});

/** Arrivals and departures over the next 7 Riyadh days, soonest first (capped at 20 each, with totals). */
export const useOwnerWeek = (unitId) => useQuery({
  queryKey: ['owner', 'week', unitId || 'all'],
  queryFn: () => get('/api/v1/bookings/owner-week', unitId ? { propertyId: unitId } : undefined)
});

/**
 * One month of a unit's calendar. `month` is 'current' or a 'YYYY-MM' the server gave us (prevMonth/nextMonth);
 * the answer carries the month's dates, weekdays and today.
 */
export const useUnitCalendar = (unitId, month) => useQuery({
  queryKey: ['owner', 'calendar', unitId, month],
  queryFn: () => get(`/api/v1/inventory/calendar/${unitId}`, { month }).then((res) => res.data),
  enabled: Boolean(unitId),
  // Keep the shown month while the next one loads — but only for the same unit.
  placeholderData: (previous, previousQuery) => (previousQuery?.queryKey?.[2] === unitId ? previous : undefined)
});

/** A unit's booking-site reviews, newest first, 20 at a time («عرض المزيد»). */
export const useUnitReviews = (unitId) => {
  const result = useInfiniteQuery({
    queryKey: ['owner', 'reviews', unitId],
    queryFn: ({ pageParam }) => get('/api/v1/ota-reviews', { propertyId: unitId, page: pageParam, limit: 20 }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last?.pagination?.hasNext ? last.pagination.page + 1 : undefined),
    enabled: Boolean(unitId)
  });
  const pages = result.data?.pages;
  return {
    ...result,
    reviews: pages ? pages.flatMap((p) => p?.reviews ?? []) : [],
    total: pages?.[0]?.pagination?.total ?? 0
  };
};

/** One booking, owner view (GET /bookings/:id gives owners its allow-list: no guest contact, no internals). */
export const useOwnerBooking = (bookingId) => useQuery({
  queryKey: ['owner', 'booking', bookingId],
  queryFn: () => get(`/api/v1/bookings/${bookingId}`).then((data) => data.booking),
  enabled: Boolean(bookingId)
});

/**
 * The owner's released monthly reports, newest month first ({_id, periodMonth, propertyCount, generatedAt}).
 * About 12 a year, so not paged (be/routes/ownerReportRoutes.js).
 */
export const useOwnerReports = () => useQuery({
  queryKey: ['owner', 'reports'],
  queryFn: () => ownerReportApi.getReports().then((data) => data.data?.reports || [])
});

/** One released report with its payload; another owner's, an unreleased or a malformed id answers 404. */
export const useOwnerReport = (reportId) => useQuery({
  queryKey: ['owner', 'report', reportId],
  queryFn: () => ownerReportApi.getReport(reportId).then((data) => data.data.report),
  enabled: Boolean(reportId),
  retry: (count, error) => error?.response?.status !== 404 && count < 2
});

/** The owner's alert switches: `{ preferences: { owner_new_booking: true, … }, types }` (absence means on). */
export const useAlertSettings = () => useQuery({
  queryKey: ['owner', 'alerts'],
  queryFn: () => notificationApi.me(),
  retry: (count, error) => error?.response?.status !== 403 && count < 2
});

/**
 * The store link for this phone, from the same public answer the app's update check reads (8d). Null until the
 * server is given one (step 13/14): a phone browser then offers nothing (Adil, 5 Nov 2026).
 */
export const useStoreLink = (platform) => useQuery({
  queryKey: ['owner', 'store-link', platform],
  queryFn: () => publicApiService.getOwnerAppConfig({ platform }),
  enabled: Boolean(platform),
  select: (answer) => (typeof answer?.storeUrl === 'string' && answer.storeUrl.startsWith('https://') ? answer.storeUrl : null),
  staleTime: 60 * 60 * 1000,
  retry: false
});
